import { ref, computed } from 'vue';
import { once } from 'lodash-es';
import type { Pfm } from '@aviata/platform-client';

import { getUserFromToken } from './lib';

let accessTokenStorageInstance: {
  setAccessToken: (nextAccessToken: string | null) => void;
  getAccessToken: () => string | null;
  clear: () => void;
} | null = null;

class AccessTokenMemoryStorage {
  private accessToken: string | null = null;

  static getInstance() {
    if (!accessTokenStorageInstance) {
      accessTokenStorageInstance = new AccessTokenMemoryStorage();
    }

    return accessTokenStorageInstance;
  }

  setAccessToken(nextAccessToken: string | null) {
    this.accessToken = nextAccessToken;
  }

  getAccessToken() {
    return this.accessToken;
  }

  clear() {
    this.accessToken = null;
  }
}

export const accessTokenMemoryStorage = AccessTokenMemoryStorage.getInstance();

/**
 * Model for user auth data management
 *
 * @param pfm - platform client instance. We need it to wait pfm init and to listen the 'user-token-update' event and save token to memory
 */
const useUserStore = (pfm: Pfm) => {
  const token = ref<string | null>(null);

  const isAuthorizationLoading = ref(false);
  const isAuthorizationError = ref(false);

  const user = computed(() => {
    if (!token.value) {
      return null;
    }

    return getUserFromToken(token.value);
  });

  /**
   * We need to init pfm instance only once.
   * The getUser method may be called in parallel from several places,
   * so we need to make sure pfm is initiated only once
   */
  const initPfmOnce = once(pfm.init);

  /** Method to return asynchronously user when it is set */
  const getUser = () => {
    if (user.value === null) {
      isAuthorizationLoading.value = true;
      return initPfmOnce()
        .then(data => {
          accessTokenMemoryStorage.setAccessToken(data.token);

          return getUserFromToken(data.token);
        })
        .catch(() => {
          isAuthorizationError.value = true;
          return null;
        })
        .finally(() => (isAuthorizationLoading.value = false));
    }
    return Promise.resolve(user.value);
  };
  /**
   * Resolves the current user's id, or REJECTS when the user can't be
   * authorized. Consumers (e.g. the avia user-store wrapper) rely on this
   * rejection to route auth failures into their Sentry `.catch` — resolving a
   * falsy id instead would log a false "Authorization completed" and swallow
   * the error.
   */
  const getUserId = () =>
    getUser().then(user => {
      if (!user) {
        throw new Error('User is not authorized');
      }
      return user.id;
    });

  const setToken = (value: string) => (token.value = value);
  const unsetToken = () => (token.value = null);
  /**
   * PFM обновил user-токен (рефреш по 401 или переавторизация).
   *
   * Кроме реактивного ref синхронизируем in-memory стор: из него читают
   * потребители, которые ходят на бэкенд НЕ через PFM-axios (напр. SSE-чат на
   * нативном fetch). Без этого стор навсегда оставался с первым токеном из
   * pfm.init(), и после рефреша такие запросы продолжали получать 401.
   */
  const onTokenUpdate = (token?: string) => {
    if (token) {
      setToken(token);
      accessTokenMemoryStorage.setAccessToken(token);
    } else {
      unsetToken();
      accessTokenMemoryStorage.clear();
    }
  };

  pfm.emitter.on('user-token-update', onTokenUpdate);

  return {
    user,
    getUser,
    getUserId,
    isAuthorizationLoading,
    isAuthorizationError,
  };
};

export const userStoreEntity = {
  model: { useUserStore },
};
